<template>
  <div id="main-wrapper">
    <NavbarDark />

    <!-- Page Title -->
    <div class="page-title">
      <div class="container">
        <div class="row">
          <div class="col-lg-12 col-md-12">
            <h2 class="ipt-title">
              <span v-if="serviceType">Book a {{ serviceType }} Professional</span>
              <span v-else>Book a Trade Professional</span>
            </h2>
            <span class="ipn-subtitle">
              <span v-if="serviceType">
                Get an experienced {{ serviceType.toLowerCase() }} expert at your door — fast, reliable, and hassle-free.
              </span>
              <span v-else>
                Fill in the details below and our team will confirm your booking within one business day.
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <section>
      <div class="container">
        <div class="row">
          <!-- Form -->
          <div class="col-lg-8 col-md-7">
            <div class="contact-form">
              <form @submit.prevent="submitRequest" novalidate>

                <!-- ── User Details ── -->
                <h5 class="form-section-title">
                  <i class="fa-solid fa-user me-2 text-primary"></i>Your Details
                </h5>
                <div class="row">
                  <div class="col-lg-6 col-md-12">
                    <div class="form-group">
                      <label>Full Name <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="John Smith"
                        v-model="formData.name"
                        :class="{ 'is-invalid': errors.name }"
                      />
                      <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                    </div>
                  </div>
                  <div class="col-lg-6 col-md-12">
                    <div class="form-group">
                      <label>Email Address <span class="text-danger">*</span></label>
                      <input
                        type="email"
                        class="form-control"
                        placeholder="john@example.com"
                        v-model="formData.email"
                        :class="{ 'is-invalid': errors.email }"
                      />
                      <div v-if="errors.email" class="invalid-feedback">{{ errors.email }}</div>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label>Phone Number <span class="text-danger">*</span></label>
                  <input
                    type="tel"
                    class="form-control"
                    placeholder="5064441234"
                    v-model="formData.phone"
                    @input="filterPhone"
                    maxlength="15"
                    :class="{ 'is-invalid': errors.phone }"
                  />
                  <div v-if="errors.phone" class="invalid-feedback">{{ errors.phone }}</div>
                </div>

                <!-- ── Property Details ── -->
                <h5 class="form-section-title mt-4">
                  <i class="fa-solid fa-house me-2 text-primary"></i>Property Details
                </h5>

                <div class="form-group">
                  <label>Service Needed <span class="text-danger">*</span></label>
                  <select
                    class="form-control"
                    v-model="formData.serviceType"
                    :class="{ 'is-invalid': errors.serviceType }"
                  >
                    <option value="" disabled>— Select a service —</option>
                    <option>Electrical</option>
                    <option>Plumbing</option>
                    <option>Lawn Care</option>
                    <option>Handyman</option>
                    <option>HVAC</option>
                    <option>Cleaning</option>
                    <option>Painting</option>
                    <option>Mini Split Cleaning</option>
                    <option>Duct Cleaning</option>
                    <option>Driveway Seal</option>
                    <option>Roofing</option>
                  </select>
                  <div v-if="errors.serviceType" class="invalid-feedback">{{ errors.serviceType }}</div>
                </div>

                <div class="form-group">
                  <label>Property Type <span class="text-danger">*</span></label>
                  <select
                    class="form-control"
                    v-model="formData.propertyType"
                    :class="{ 'is-invalid': errors.propertyType }"
                  >
                    <option value="" disabled>— Select property type —</option>
                    <option>Single Family Home</option>
                    <option>Townhouse</option>
                    <option>Apartment / Condo</option>
                    <option>Commercial</option>
                    <option>Other</option>
                  </select>
                  <div v-if="errors.propertyType" class="invalid-feedback">{{ errors.propertyType }}</div>
                </div>

                <!-- ── Location ── -->
                <h5 class="form-section-title mt-4">
                  <i class="fa-solid fa-location-dot me-2 text-primary"></i>Property Location
                </h5>

                <div class="form-group">
                  <label>Street Address <span class="text-danger">*</span></label>
                  <input
                    id="trade-address-input"
                    type="text"
                    class="form-control"
                    placeholder="Start typing your address..."
                    v-model="formData.address"
                    :class="{ 'is-invalid': errors.address }"
                    autocomplete="off"
                  />
                  <div v-if="errors.address" class="invalid-feedback">{{ errors.address }}</div>
                </div>

                <div class="row">
                  <div class="col-lg-5 col-md-12">
                    <div class="form-group">
                      <label>City <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="Moncton"
                        v-model="formData.city"
                        :class="{ 'is-invalid': errors.city }"
                      />
                      <div v-if="errors.city" class="invalid-feedback">{{ errors.city }}</div>
                    </div>
                  </div>
                  <div class="col-lg-4 col-md-12">
                    <div class="form-group">
                      <label>Province / State <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="NB"
                        v-model="formData.province"
                        :class="{ 'is-invalid': errors.province }"
                      />
                      <div v-if="errors.province" class="invalid-feedback">{{ errors.province }}</div>
                    </div>
                  </div>
                  <div class="col-lg-3 col-md-12">
                    <div class="form-group">
                      <label>Postal Code <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="E1C 3B9"
                        v-model="formData.postalCode"
                        :class="{ 'is-invalid': errors.postalCode }"
                      />
                      <div v-if="errors.postalCode" class="invalid-feedback">{{ errors.postalCode }}</div>
                    </div>
                  </div>
                </div>

                <!-- ── Scheduling ── -->
                <h5 class="form-section-title mt-4">
                  <i class="fa-solid fa-calendar-week me-2 text-primary"></i>Scheduling
                </h5>

                <div class="row">
                  <div class="col-lg-6 col-md-12">
                    <div class="form-group">
                      <label>Preferred Date</label>
                      <input
                        type="date"
                        class="form-control"
                        v-model="formData.preferredDate"
                        :min="today"
                      />
                    </div>
                  </div>
                  <div class="col-lg-6 col-md-12">
                    <div class="form-group">
                      <label>Urgency <span class="text-danger">*</span></label>
                      <select
                        class="form-control"
                        v-model="formData.urgency"
                        :class="{ 'is-invalid': errors.urgency }"
                      >
                        <option value="" disabled>— Select urgency —</option>
                        <option>Flexible (within the month)</option>
                        <option>Within a Week</option>
                        <option>ASAP (Emergency)</option>
                      </select>
                      <div v-if="errors.urgency" class="invalid-feedback">{{ errors.urgency }}</div>
                    </div>
                  </div>
                </div>

                <!-- ── Issue Description ── -->
                <h5 class="form-section-title mt-4">
                  <i class="fa-solid fa-comment-dots me-2 text-primary"></i>Tell Us More
                </h5>

                <div class="form-group">
                  <label>Describe the Issue or Work Needed <span class="text-danger">*</span></label>
                  <textarea
                    class="form-control"
                    rows="5"
                    placeholder="Please describe what needs to be done, any relevant details, and the best time to reach you..."
                    v-model="formData.description"
                    :class="{ 'is-invalid': errors.description }"
                  ></textarea>
                  <div v-if="errors.description" class="invalid-feedback">{{ errors.description }}</div>
                </div>

                <!-- ── Attachments (optional) ── -->
                <h5 class="form-section-title mt-4">
                  <i class="fa-solid fa-camera me-2 text-primary"></i>Photos
                  <small class="text-muted fw-normal ms-1">(optional, up to 2 images)</small>
                </h5>

                <div class="form-group">
                  <div
                    class="attach-dropzone"
                    :class="{ 'attach-dropzone--full': attachments.length >= 2 }"
                    @click="attachments.length < 2 && triggerFileInput()"
                    @dragover.prevent
                    @drop.prevent="handleDrop"
                  >
                    <i class="fa-solid fa-cloud-arrow-up"></i>
                    <p class="mb-0">Click to upload or drag &amp; drop</p>
                    <span>PNG, JPG — max 5 MB each &middot; 2 images max</span>
                    <input
                      ref="fileInputRef"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      multiple
                      style="display:none"
                      @change="handleFileSelect"
                    />
                  </div>
                  <div v-if="errors.attachments" class="text-danger small mt-1">{{ errors.attachments }}</div>

                  <!-- Previews -->
                  <div v-if="attachments.length" class="attach-previews mt-3">
                    <div v-for="(att, idx) in attachments" :key="idx" class="attach-preview-item">
                      <img :src="att.preview" :alt="att.name" />
                      <button type="button" class="attach-remove" @click="removeAttachment(idx)" aria-label="Remove">
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                      <span class="attach-name">{{ att.name }}</span>
                    </div>
                  </div>
                </div>

                <div class="form-group mt-3">
                  <button
                    type="submit"
                    class="btn btn-primary btn-lg"
                    :disabled="isSending"
                  >
                    <span v-if="isSending" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {{ isSending ? "Submitting..." : "Submit Request" }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-lg ms-3"
                    @click="goBack"
                  >
                    Back
                  </button>
                </div>

              </form>
            </div>
          </div>

          <!-- Sidebar Info -->
          <div class="col-lg-4 col-md-5">
            <div class="contact-info">
              <h2>What to Expect</h2>
              <p>Submit your request and our team will get back to you soon to confirm availability and pricing.</p>

              <div class="cn-info-detail mt-4">
                <div class="cn-info-icon">
                  <i class="fa-solid fa-clipboard-check"></i>
                </div>
                <div class="cn-info-content">
                  <h4 class="cn-info-title">1. Submit the Form</h4>
                  Fill in your details and describe the work needed.
                </div>
              </div>

              <div class="cn-info-detail">
                <div class="cn-info-icon">
                  <i class="fa-solid fa-phone-volume"></i>
                </div>
                <div class="cn-info-content">
                  <h4 class="cn-info-title">2. We'll Contact You</h4>
                  Our team will call or email to confirm and schedule.
                </div>
              </div>

              <div class="cn-info-detail">
                <div class="cn-info-icon">
                  <i class="fa-solid fa-hard-hat"></i>
                </div>
                <div class="cn-info-content">
                  <h4 class="cn-info-title">3. Job Done Right</h4>
                  A vetted professional completes the work at your property.
                </div>
              </div>

              <div class="cn-info-detail mt-3">
                <div class="cn-info-icon">
                  <i class="fa-solid fa-envelope-circle-check"></i>
                </div>
                <div class="cn-info-content">
                  <h4 class="cn-info-title">Direct Contact</h4>
                  rent@99properties.ca
                </div>
              </div>

              <div class="cn-info-detail">
                <div class="cn-info-icon">
                  <i class="fa-solid fa-phone"></i>
                </div>
                <div class="cn-info-content">
                  <h4 class="cn-info-title">Call Us</h4>
                  (+1) 506 889 7999
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <FooterDark />
    <ScrollToTop />
  </div>
</template>

<script setup>
import NavbarDark from "@/components/navbar/navbar-dark.vue";
import FooterDark from "@/components/footer/footer-dark.vue";
import ScrollToTop from "@/components/scroll-to-top.vue";
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

const route = useRoute();
const router = useRouter();

const serviceType = ref("");
const isSending = ref(false);
const errors = ref({});
const attachments = ref([]); // { file: File, preview: string, name: string }
const fileInputRef = ref(null);

const triggerFileInput = () => fileInputRef.value?.click();

const addFiles = (fileList) => {
  const incoming = Array.from(fileList);
  for (const file of incoming) {
    if (attachments.value.length >= 2) {
      errors.value.attachments = "You can upload a maximum of 2 images.";
      break;
    }
    if (!file.type.startsWith("image/")) {
      errors.value.attachments = "Only PNG or JPG images are allowed.";
      continue;
    }
    if (file.size > 5 * 1024 * 1024) {
      errors.value.attachments = `"${file.name}" exceeds the 5 MB limit.`;
      continue;
    }
    if (errors.value.attachments) delete errors.value.attachments;
    attachments.value.push({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    });
  }
};

const handleFileSelect = (event) => {
  addFiles(event.target.files);
  // reset input so the same file can be re-added after removal
  event.target.value = "";
};

const handleDrop = (event) => addFiles(event.dataTransfer.files);

const removeAttachment = (idx) => {
  URL.revokeObjectURL(attachments.value[idx].preview);
  attachments.value.splice(idx, 1);
  if (errors.value.attachments) delete errors.value.attachments;
};

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const today = new Date().toISOString().split("T")[0];

const formData = ref({
  name: "",
  email: "",
  phone: "",
  serviceType: "",
  propertyType: "",
  address: "",
  city: "",
  province: "",
  postalCode: "",
  preferredDate: "",
  urgency: "",
  description: "",
});

onMounted(() => {
  // Pre-fill service type from query param (e.g. ?service=Electrical)
  if (route.query.service) {
    serviceType.value = route.query.service;
    formData.value.serviceType = route.query.service;
  }
  loadGoogleMapsScript();
});

// ── Google Places Autocomplete ──
const provinceMap = {
  AB: "Alberta", BC: "British Columbia", MB: "Manitoba",
  NB: "New Brunswick", NL: "Newfoundland and Labrador",
  NS: "Nova Scotia", ON: "Ontario", PE: "Prince Edward Island",
  QC: "Quebec", SK: "Saskatchewan",
  NT: "Northwest Territories", NU: "Nunavut", YT: "Yukon",
};

const initAutocomplete = () => {
  if (typeof google === "undefined" || typeof window.google.maps === "undefined") {
    setTimeout(initAutocomplete, 100);
    return;
  }
  const addressInput = document.getElementById("trade-address-input");
  if (!addressInput) return;

  const autocomplete = new window.google.maps.places.Autocomplete(addressInput, {
    types: ["address"],
    componentRestrictions: { country: "ca" },
  });

  autocomplete.addListener("place_changed", () => {
    const place = autocomplete.getPlace();
    if (!place.geometry) return;

    formData.value.address = place.formatted_address;
    if (errors.value.address) delete errors.value.address;

    for (const component of place.address_components) {
      const type = component.types[0];
      if (type === "locality") formData.value.city = component.long_name;
      if (type === "administrative_area_level_1")
        formData.value.province = provinceMap[component.short_name] || component.long_name;
      if (type === "postal_code") formData.value.postalCode = component.long_name;
    }
  });
};

const loadGoogleMapsScript = () => {
  if (window.google && window.google.maps) {
    initAutocomplete();
    return;
  }
  const apiKey = process.env.VUE_APP_mapKEY;
  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = () => initAutocomplete();
  script.onerror = () => console.error("Failed to load Google Maps script.");
  document.head.appendChild(script);
};

const filterPhone = (event) => {
  formData.value.phone = event.target.value.replace(/[^0-9]/g, "");
};

const validate = () => {
  const e = {};

  if (!formData.value.name.trim()) e.name = "Full name is required.";

  if (!formData.value.email.trim()) {
    e.email = "Email is required.";
  } else if (!/^\S+@\S+\.\S+$/.test(formData.value.email)) {
    e.email = "Please enter a valid email address.";
  }

  if (!formData.value.phone) {
    e.phone = "Phone number is required.";
  } else if (formData.value.phone.length < 10) {
    e.phone = "Please enter a valid phone number (at least 10 digits).";
  }

  if (!formData.value.serviceType) e.serviceType = "Please select a service.";
  if (!formData.value.propertyType) e.propertyType = "Please select a property type.";
  if (!formData.value.address.trim()) e.address = "Street address is required.";
  if (!formData.value.city.trim()) e.city = "City is required.";
  if (!formData.value.province.trim()) e.province = "Province / State is required.";
  if (!formData.value.postalCode.trim()) e.postalCode = "Postal code is required.";
  if (!formData.value.urgency) e.urgency = "Please select an urgency level.";
  if (!formData.value.description.trim()) e.description = "Please describe the work needed.";

  return e;
};

const submitRequest = async () => {
  errors.value = validate();
  if (Object.keys(errors.value).length > 0) return;

  const fullAddress = `${formData.value.address}, ${formData.value.city}, ${formData.value.province} ${formData.value.postalCode}`;
  const descriptionBody = [
    `Service Requested: ${formData.value.serviceType}`,
    `Property Type: ${formData.value.propertyType}`,
    `Property Address: ${fullAddress}`,
    `Urgency: ${formData.value.urgency}`,
    formData.value.preferredDate ? `Preferred Date: ${formData.value.preferredDate}` : null,
    ``,
    `Issue / Work Description:`,
    formData.value.description,
  ]
    .filter((line) => line !== null)
    .join("\n");

  const attachmentData = await Promise.all(
    attachments.value.map(async (a) => ({
      name: a.name,
      base64: await toBase64(a.file),
      type: a.file.type,
    }))
  );

  const requestBody = {
    name: formData.value.name,
    email: formData.value.email,
    phone: formData.value.phone,
    subject: `Services Lead — ${formData.value.serviceType} Request`,
    address: fullAddress,
    description: descriptionBody,
    attachments: attachmentData,
  };

  try {
    isSending.value = true;
    const response = await fetch(process.env.VUE_APP_sendEmailURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) throw new Error(response.statusText);

    Swal.fire({
      title: "Request Submitted!",
      text: "Thank you! We'll be in touch within one business day to confirm your booking.",
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#0d6efd",
    }).then((result) => {
      if (result.isConfirmed) router.push("/");
    });
  } catch {
    Swal.fire({
      title: "Error!",
      text: "Failed to submit your request. Please try again or call us directly.",
      icon: "error",
      confirmButtonText: "OK",
    });
  } finally {
    isSending.value = false;
  }
};

const goBack = () => {
  router.push("/trade-services");
};
</script>

<style scoped>
.form-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 8px;
  margin-bottom: 1rem;
}

/* ── Attachment dropzone ── */
.attach-dropzone {
  border: 2px dashed #adb5bd;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #f8fafd;
  color: #6c757d;
}
.attach-dropzone:hover:not(.attach-dropzone--full) {
  border-color: #0d6efd;
  background: #eef4ff;
  color: #0d6efd;
}
.attach-dropzone--full {
  cursor: not-allowed;
  opacity: 0.55;
}
.attach-dropzone i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
}
.attach-dropzone span {
  font-size: 0.78rem;
}

/* ── Previews ── */
.attach-previews {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.attach-preview-item {
  position: relative;
  width: 120px;
  text-align: center;
}
.attach-preview-item img {
  width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  display: block;
}
.attach-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #dc3545;
  color: #fff;
  border: none;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 1;
}
.attach-name {
  font-size: 0.72rem;
  color: #6c757d;
  display: block;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}
</style>
